import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Calendar,
  ChevronRight,
  DollarSign,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { financeApi } from '@/services/finance'
import { workersApi } from '@/services/workers'
import { SearchableSelect } from '@/components/common/searchable-select'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn, formatCurrency } from '@/lib/utils'
import { useOrganizationDateLocale } from '@/lib/org-date-locale'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import { getErrorMessage } from '@/lib/api'

// ─── Types ──────────────────────────────────────────────────────────────────

type DailyInvoicePayment = {
  id?: string | number
  invoice_id?: string | number
  amount?: string | number | null
  payment_method?: string | null
  payment_method_label?: string | null
  paid_on?: string | null
  payment_ref?: string | null
  transaction_id?: string | null
  remarks?: string | null
}

type DailyInvoiceRow = {
  id: string | number
  invoice_ref?: string | null
  worker_id?: string | number | null
  worker_name?: string | null
  worker_user_id?: string | number | null
  period_start?: string | null
  period_end?: string | null
  jobs_count?: number
  total_earned?: string | number | null
  total_paid?: string | number | null
  pending_amount?: string | number | null
  balance?: string | number | null
  status?: 'paid' | 'partial' | 'pending' | 'overpaid' | string
  closed_on?: string | null
  last_activity?: string | null
  payments?: DailyInvoicePayment[]
}

type DailyInvoiceLine = {
  id: string | number
  amount?: string | number | null
  workday_entry?: {
    id?: string | number | null
    workday_entry_ref?: string | null
    work_date?: string | null
    hours?: string | number | null
    entry_total_pay?: string | number | null
    invoiced_amount?: string | number | null
    review_status?: string | null
    workflow_status_code?: string | null
  }
  job?: {
    id?: string | number | null
    job_ref?: string | null
    title?: string | null
  }
  assignment?: {
    id?: string | number | null
    assignment_ref?: string | null
  }
}

type DailyInvoiceDetail = {
  invoice: DailyInvoiceRow
  lines_count: number
  lines: DailyInvoiceLine[]
}

type DailyInvoiceListResponse = {
  count?: number
  results?: DailyInvoiceRow[]
  summary?: {
    total_payout?: string | number | null
    total_paid?: string | number | null
    total_pending?: string | number | null
    invoice_count?: number
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const toNumber = (v?: string | number | null) => {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

const PAYMENT_STATUS_OPTIONS = {
  all: 'All statuses',
  pending: 'Pending',
  partial: 'Partial',
  paid: 'Paid',
  overpaid: 'Overpaid',
} as const

function StatusBadge({ status }: { status?: string | null }) {
  const normalized = String(status || 'pending').toLowerCase()
  const label =
    PAYMENT_STATUS_OPTIONS[normalized as keyof typeof PAYMENT_STATUS_OPTIONS] ??
    normalized.replace(/_/g, ' ')
  return (
    <Badge
      variant="outline"
      className={cn(
        'capitalize',
        normalized === 'paid' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
        normalized === 'partial' && 'border-blue-200 bg-blue-50 text-blue-700',
        normalized === 'pending' && 'border-amber-200 bg-amber-50 text-amber-700',
        normalized === 'overpaid' && 'border-purple-200 bg-purple-50 text-purple-700',
      )}
    >
      {label}
    </Badge>
  )
}

function formatDateShort(value?: string | null, locale = 'en-US') {
  if (!value) return '-'
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(d)
}

// ─── Invoice detail sheet ────────────────────────────────────────────────────

function DailyInvoiceDetailSheet({
  invoiceId,
  serviceInstanceId,
  open,
  onOpenChange,
  locale,
}: {
  invoiceId: string | number | null
  serviceInstanceId: string | null | undefined
  open: boolean
  onOpenChange: (v: boolean) => void
  locale: string
}) {
  const detailQuery = useQuery<DailyInvoiceDetail>({
    queryKey: ['worker-daily-invoice-detail', invoiceId, serviceInstanceId],
    queryFn: () => financeApi.getWorkerDailyInvoiceDetail(invoiceId!, serviceInstanceId),
    enabled: open && invoiceId != null,
  })

  const inv = detailQuery.data?.invoice
  const lines = detailQuery.data?.lines ?? []

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            Daily Invoice
            {inv?.invoice_ref ? (
              <span className="text-sm font-normal text-muted-foreground">
                #{inv.invoice_ref}
              </span>
            ) : null}
          </SheetTitle>
        </SheetHeader>

        {detailQuery.isPending && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        )}

        {detailQuery.isError && (
          <p className="text-sm text-destructive">
            Failed to load invoice details. {getErrorMessage(detailQuery.error)}
          </p>
        )}

        {inv && (
          <>
            {/* Header cards */}
            <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Worker</p>
                <p className="font-medium">{inv.worker_name ?? '-'}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Period</p>
                <p className="font-medium">{formatDateShort(inv.period_start, locale)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Earned</p>
                <p className="font-medium text-emerald-700">
                  {formatCurrency(toNumber(inv.total_earned))}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Status</p>
                <StatusBadge status={inv.status} />
              </div>
            </div>

            {/* Work lines table */}
            <h3 className="text-sm font-medium mb-2">
              Work Entries ({detailQuery.data?.lines_count ?? lines.length})
            </h3>
            {lines.length === 0 ? (
              <p className="text-sm text-muted-foreground">No work entries linked.</p>
            ) : (
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs uppercase tracking-wider">
                      <TableHead>Date</TableHead>
                      <TableHead>Job</TableHead>
                      <TableHead>Ref</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDateShort(line.workday_entry?.work_date, locale)}
                        </TableCell>
                        <TableCell className="text-xs">
                          <span className="font-medium">
                            {line.job?.title ?? line.job?.job_ref ?? (line.job?.id ? `Job #${line.job.id}` : '-')}
                          </span>
                          {line.assignment?.assignment_ref ? (
                            <span className="block text-muted-foreground text-xs">
                              {line.assignment.assignment_ref}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">
                          {line.workday_entry?.workday_entry_ref ?? '-'}
                        </TableCell>
                        <TableCell className="text-xs text-right">
                          {toNumber(line.workday_entry?.hours) > 0
                            ? toNumber(line.workday_entry?.hours).toFixed(2)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-xs text-right font-medium">
                          {formatCurrency(toNumber(line.amount))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Totals */}
            <div className="mt-4 flex flex-col items-end gap-1 text-sm">
              <div className="flex justify-between w-48">
                <span className="text-muted-foreground">Total earned</span>
                <span className="font-medium">{formatCurrency(toNumber(inv.total_earned))}</span>
              </div>
              <div className="flex justify-between w-48">
                <span className="text-muted-foreground">Paid</span>
                <span className="font-medium text-emerald-700">{formatCurrency(toNumber(inv.total_paid))}</span>
              </div>
              <div className="flex justify-between w-48 border-t pt-1 font-semibold">
                <span>Pending</span>
                <span>{formatCurrency(toNumber(inv.pending_amount))}</span>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ─── Generate dialog ─────────────────────────────────────────────────────────

function GenerateDailyInvoicesPanel({
  serviceInstanceId,
  onSuccess,
}: {
  serviceInstanceId: string | null | undefined
  onSuccess: () => void
}) {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const generateMutation = useMutation({
    mutationFn: () =>
      financeApi.generateWorkerDailyInvoices(
        { date_from: dateFrom, date_to: dateTo },
        serviceInstanceId,
      ),
    onSuccess: (result) => {
      const created = result?.created_invoices ?? 0
      const linked = result?.linked_entries ?? 0
      toast.success(`Generated ${created} invoice(s), linked ${linked} work entr${linked === 1 ? 'y' : 'ies'}.`)
      onSuccess()
    },
    onError: (err) => {
      toast.error(getErrorMessage(err) || 'Failed to generate daily invoices.')
    },
  })

  return (
    <div className="rounded-xl border bg-muted/30 p-4 flex flex-wrap gap-4 items-end">
      <div className="flex flex-col gap-1 min-w-[140px]">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider">From date</Label>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div className="flex flex-col gap-1 min-w-[140px]">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider">To date</Label>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <Button
        size="sm"
        disabled={!dateFrom || !dateTo || generateMutation.isPending}
        onClick={() => generateMutation.mutate()}
      >
        {generateMutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
        )}
        Generate invoices
      </Button>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function WorkerDailyInvoicesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const locale = useOrganizationDateLocale(serviceInstanceId)
  const queryClient = useQueryClient()

  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [selectedWorkerId, setSelectedWorkerId] = useState(searchParams.get('worker_id') || 'all')
  const [paymentStatus, setPaymentStatus] = useState(searchParams.get('payment_status') || 'all')
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '')
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '')
  const [page, setPage] = useState(() => {
    const v = Number(searchParams.get('page') || '1')
    return Number.isFinite(v) && v > 0 ? v : 1
  })
  const [pageSize, setPageSize] = useState(() => {
    const v = Number(searchParams.get('page_size') || '25')
    return Number.isFinite(v) && v > 0 ? v : 25
  })
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | number | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const deferredSearch = useDeferredValue(search.trim())

  // Sync state from URL
  useEffect(() => {
    setSearch(searchParams.get('search') || '')
    setSelectedWorkerId(searchParams.get('worker_id') || 'all')
    setPaymentStatus(searchParams.get('payment_status') || 'all')
    setDateFrom(searchParams.get('date_from') || '')
    setDateTo(searchParams.get('date_to') || '')
    const p = Number(searchParams.get('page') || '1')
    setPage(Number.isFinite(p) && p > 0 ? p : 1)
    const ps = Number(searchParams.get('page_size') || '25')
    setPageSize(Number.isFinite(ps) && ps > 0 ? ps : 25)
  }, [searchParams])

  // Sync URL from state
  useEffect(() => {
    const params = new URLSearchParams()
    if (serviceInstanceId) params.set('service_instance_id', serviceInstanceId)
    if (deferredSearch) params.set('search', deferredSearch)
    if (selectedWorkerId !== 'all') params.set('worker_id', selectedWorkerId)
    if (paymentStatus !== 'all') params.set('payment_status', paymentStatus)
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (page > 1) params.set('page', String(page))
    if (pageSize !== 25) params.set('page_size', String(pageSize))
    const next = params.toString()
    const current = location.search.startsWith('?') ? location.search.slice(1) : location.search
    if (next !== current) {
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true })
    }
  }, [dateFrom, dateTo, deferredSearch, location.pathname, location.search, navigate, page, pageSize, paymentStatus, selectedWorkerId, serviceInstanceId])

  const workersQuery = useQuery({
    queryKey: ['daily-invoices-workers', serviceInstanceId],
    queryFn: () =>
      workersApi.getWorkers({ page_size: 200, active: true, service_instance_id: serviceInstanceId ?? undefined }),
    staleTime: 60_000,
  })

  const workerOptions = useMemo(
    () => [
      { value: 'all', label: 'All workers', keywords: 'all workers' },
      ...(workersQuery.data?.results ?? []).map((w) => ({
        value: String(w.id),
        label: getUserDropdownLabel(w, w.id),
        keywords: getUserDropdownKeywords(w),
      })),
    ],
    [workersQuery.data?.results],
  )

  const listQuery = useQuery<DailyInvoiceListResponse>({
    queryKey: [
      'worker-daily-invoices',
      page,
      pageSize,
      selectedWorkerId,
      paymentStatus,
      dateFrom,
      dateTo,
      deferredSearch,
      serviceInstanceId,
    ],
    queryFn: () =>
      financeApi.getWorkerDailyInvoiceList({
        page,
        page_size: pageSize,
        worker_id: selectedWorkerId !== 'all' ? selectedWorkerId : undefined,
        payment_status: paymentStatus !== 'all' ? paymentStatus : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: deferredSearch || undefined,
        service_instance_id: serviceInstanceId ?? undefined,
      }),
    enabled: Boolean(serviceInstanceId),
  })

  const rows = listQuery.data?.results ?? []
  const summary = listQuery.data?.summary
  const totalCount = listQuery.data?.count ?? rows.length

  const openDetail = (id: string | number) => {
    setSelectedInvoiceId(id)
    setSheetOpen(true)
  }

  const hasFilters = deferredSearch || selectedWorkerId !== 'all' || paymentStatus !== 'all' || dateFrom || dateTo

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Daily Worker Invoices</h1>
        <p className="text-sm text-muted-foreground">
          One invoice per worker per day — aggregated from accepted work entries.
        </p>
      </div>

      {/* Generate panel */}
      <GenerateDailyInvoicesPanel
        serviceInstanceId={serviceInstanceId}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['worker-daily-invoices'] })}
      />

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total payout', value: formatCurrency(toNumber(summary.total_payout)), color: '' },
            { label: 'Paid', value: formatCurrency(toNumber(summary.total_paid)), color: 'text-emerald-700' },
            { label: 'Pending', value: formatCurrency(toNumber(summary.total_pending)), color: 'text-amber-600' },
            { label: 'Invoices', value: String(summary.invoice_count ?? totalCount), color: '' },
          ].map(({ label, value, color }) => (
            <Card key={label}>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
                <p className={cn('text-lg font-semibold', color)}>{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative min-w-[180px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search workers…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="pl-8 h-8 text-sm"
          />
        </div>

        <SearchableSelect
          options={workerOptions}
          value={selectedWorkerId}
          onValueChange={(v) => { setSelectedWorkerId(v); setPage(1) }}
          placeholder="All workers"
          triggerClassName="h-8 text-sm min-w-[160px]"
        />

        <Select value={paymentStatus} onValueChange={(v) => { setPaymentStatus(v); setPage(1) }}>
          <SelectTrigger className="h-8 text-sm min-w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PAYMENT_STATUS_OPTIONS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
            className="h-8 text-sm w-[130px]"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
            className="h-8 text-sm w-[130px]"
          />
        </div>

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => {
              setSearch('')
              setSelectedWorkerId('all')
              setPaymentStatus('all')
              setDateFrom('')
              setDateTo('')
              setPage(1)
            }}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="text-xs uppercase tracking-wider">
              <TableHead>Ref</TableHead>
              <TableHead>Worker</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-center">Jobs</TableHead>
              <TableHead className="text-right">Earned</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Pending</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {listQuery.isPending
              ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.length === 0
              ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-12">
                    {hasFilters ? 'No invoices match the selected filters.' : 'No daily invoices yet. Use the panel above to generate them.'}
                  </TableCell>
                </TableRow>
              )
              : rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => openDetail(row.id)}
                >
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    #{row.invoice_ref || String(row.id).padStart(6, '0')}
                  </TableCell>
                  <TableCell className="font-medium text-sm">
                    {row.worker_name ?? `Worker #${row.worker_id ?? '-'}`}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {formatDateShort(row.period_start, locale)}
                  </TableCell>
                  <TableCell className="text-center text-sm">{row.jobs_count ?? '-'}</TableCell>
                  <TableCell className="text-right text-sm font-medium">
                    {formatCurrency(toNumber(row.total_earned))}
                  </TableCell>
                  <TableCell className="text-right text-sm text-emerald-700">
                    {formatCurrency(toNumber(row.total_paid))}
                  </TableCell>
                  <TableCell className="text-right text-sm text-amber-600">
                    {formatCurrency(toNumber(row.pending_amount))}
                  </TableCell>
                  <TableCell><StatusBadge status={row.status} /></TableCell>
                  <TableCell>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalCount > pageSize && (
        <DataTablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(ps) => { setPageSize(ps); setPage(1) }}
        />
      )}

      {/* Detail sheet */}
      <DailyInvoiceDetailSheet
        invoiceId={selectedInvoiceId}
        serviceInstanceId={serviceInstanceId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        locale={locale}
      />
    </div>
  )
}
